/**
 * Field Export Mappings Preview Component (Read-only)
 * Displays the configured export mappings for reference fields
 */

import { Label } from '@/components/ui/label';
import { ExportTemplate } from '@/types/exportTemplates';

interface FieldExportMappingsPreviewProps {
  template: ExportTemplate;
}

const REFERENCE_TYPES = [
  { 
    key: 'glossaryItem', 
    label: 'Glossary Items (Object References)',
    description: 'When a field references another object from your library'
  },
  { 
    key: 'types', 
    label: 'Types',
    description: 'When a field references a type'
  },
  { 
    key: 'tags', 
    label: 'Tags',
    description: 'When a field references a tag'
  }
];

const PROPERTY_LABELS: Record<string, string> = {
  'id': 'ID (Unique Identifier)',
  'name': 'Name (Display Name)',
  'code_id': 'Code ID (Code Reference)'
};

export const FieldExportMappingsPreview = ({ template }: FieldExportMappingsPreviewProps) => {
  const mappings = template.fieldExportMappings || {};

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-base font-semibold mb-2 block">Reference Field Export Settings</Label>
        <p className="text-sm text-muted-foreground mb-4">
          Configuration for which property to export when a field references another object.
        </p>
      </div>

      <div className="space-y-3">
        {REFERENCE_TYPES.map(({ key, label, description }) => {
          const mappedProperty = mappings[key] || 'name';
          return (
            <div key={key} className="p-4 border border-border rounded bg-accent/5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <Label className="text-sm font-medium">{label}</Label>
                  <p className="text-xs text-muted-foreground mt-1">{description}</p>
                </div>
                <div className="w-64">
                  <div className="p-2 bg-accent/50 rounded text-sm">
                    {PROPERTY_LABELS[mappedProperty] || mappedProperty}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="p-3 bg-muted/30 rounded text-xs text-muted-foreground">
        <strong>Note:</strong> These settings determine which property gets exported when fields reference other objects. For example, if set to "Name", a field referencing an NPC will export the NPC's name instead of its ID.
      </div>
    </div>
  );
};
